import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Btn, Loading, Msg, Section, useAdminGet } from '../../kit';
import { Badge, Check, inputCls } from '../emailKit';
import { TemplatePicker } from '../content/TemplatePicker';
import {
  EMPTY_EDITABLE, FORMAT_LABEL, LibraryMeta, LibraryRow, SourceContent, defaultEnvelope, fromSource,
} from '../content/types';
import { LibraryTemplateEditor, Seed } from './LibraryTemplateEditor';

/**
 * Saved templates — the one library Campaigns and MEMAC share (owner,
 * 2026-10-14: "reuse templates from the campaigns we have built … with the
 * full option to create templates like we have for campaigns"). Backend:
 * email_library (table email_template_library, keys lib_…, merged into the
 * template catalogue, so every template list offers them).
 *
 * Mounted as Email Center → Saved templates and under MEMAC → Templates
 * (compact). A new template starts blank or from anything "Start from…"
 * offers: another saved template, a built-in e-mail, a past campaign or an
 * e-mail design. The built-in e-mails themselves stay in Templates
 * (editable); they can be copied in here.
 */
export const TemplateLibrary: React.FC<{
  /** Where it is mounted: new templates record it as their origin. */
  origin?: 'library' | 'campaigns' | 'memac';
  /** Open this template (a deep link, or MEMAC's "Edit template"). */
  initialKey?: string;
  compact?: boolean;
}> = ({ origin = 'library', initialKey, compact }) => {
  const [archived, setArchived] = useState(false);
  const q = useAdminGet<LibraryMeta>('email', 'library_list', archived ? { include_archived: 1 } : {}, [archived]);
  const meta = q.data;
  const list: LibraryRow[] = meta?.templates || [];
  const [search, setSearch] = useState('');
  const [sel, setSel] = useState<string | null>(initialKey || null);
  const [seed, setSeed] = useState<Seed | null>(null);
  const [picker, setPicker] = useState(false);
  const dirty = useRef(false);
  const nonce = useRef(0);

  const leave = () => !dirty.current || window.confirm('Discard the unsaved changes to this template?');
  const open = (key: string) => {
    if (key === sel && !seed) return;
    if (!leave()) return;
    dirty.current = false;
    setSeed(null); setSel(key);
  };
  const startNew = (s: Omit<Seed, 'nonce'>) => {
    if (!leave()) return;
    dirty.current = false;
    nonce.current += 1;
    setSel(null); setSeed({ ...s, nonce: nonce.current });
  };
  const blank = () => startNew({
    content: { ...EMPTY_EDITABLE, format: 'html' }, envelope: defaultEnvelope(meta?.senders || []),
    source: null, name: '', category: '',
  });
  const fromPick = (src: SourceContent) => {
    setPicker(false);
    const s = fromSource(src);
    const copyName = src.kind === 'library' || src.kind === 'template' ? `${src.name} (copy)` : src.name;
    startNew({ content: s.content, envelope: s.envelope.from_email ? s.envelope : defaultEnvelope(meta?.senders || []),
      source: s.source, name: copyName.slice(0, 120), category: s.category, fallbacks: s.fallbacks });
  };

  // A deep link (or MEMAC's "Edit template") picks that template.
  useEffect(() => { if (initialKey) open(initialKey); }, [initialKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // An archived one asked for by link: show the archived ones too.
  useEffect(() => {
    if (sel && meta && !archived && !list.some(t => t.key === sel) && sel === initialKey) setArchived(true);
  }, [meta]); // eslint-disable-line react-hooks/exhaustive-deps
  // Nothing open: the first one.
  useEffect(() => {
    if (!sel && !seed && list.length) setSel(list[0].key);
  }, [list.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const cats = meta?.categories || [];
  const shown = list.filter(t => {
    const n = search.trim().toLowerCase();
    return !n || [t.name, t.key, t.subject, t.description].some(s => (s || '').toLowerCase().includes(n));
  });
  // By category, in MEMAC's order; uncategorised last.
  const groups = useMemo(() => {
    const order = [...cats.map(c => c.key), ''];
    return order.map(k => ({
      key: k, label: k ? (cats.find(c => c.key === k)?.label || k) : 'No category',
      items: shown.filter(t => (order.includes(t.category || '') ? (t.category || '') : '') === k),
    })).filter(g => g.items.length);
  }, [shown, cats]);

  const saved = (t: LibraryRow) => {
    dirty.current = false;
    q.reload();
    setSeed(null); setSel(t.key);
  };

  return (
    <Section title={compact ? 'Saved templates (shared with Campaigns)' : 'Saved templates — shared by Campaigns and MEMAC'}
      right={<div className="flex items-center gap-2">
        {meta && <span className="text-xs text-grayscale-1100">{meta.limits.active} of {meta.limits.templates}</span>}
        <Btn small onClick={q.reload}>Refresh</Btn>
      </div>}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        A saved template can be sent by a campaign (Campaigns → Use a template) and by a MEMAC trigger
        (“A saved template”). Edit it here: every send after a save uses the new version. Start one blank,
        or from a built-in e-mail, a past campaign or a design.
      </p>
      {q.error && <Msg kind="error">{q.error}</Msg>}

      <div className={`grid gap-4 ${compact ? 'lg:grid-cols-[15rem_minmax(0,1fr)]' : 'lg:grid-cols-[17rem_minmax(0,1fr)]'}`}>
        <div className="flex flex-col gap-2 min-w-0">
          <div className="flex gap-1.5 flex-wrap">
            <Btn small kind="primary" onClick={blank} disabled={!meta}>+ New template</Btn>
            <Btn small onClick={() => setPicker(true)} disabled={!meta}>Start from…</Btn>
          </div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Find a saved template…" className={inputCls} />
          <Check checked={archived} onChange={setArchived}>Show archived</Check>
          <div className="flex flex-col gap-3 max-h-[75vh] overflow-y-auto pr-1">
            {seed && !sel && (
              <div className="rounded-lg border border-primary-500 bg-primary-50 px-3 py-2 text-sm text-grayscale-1300">
                {seed.name || 'New template'} <Badge tone="amber">not saved</Badge>
              </div>
            )}
            {q.loading && !meta ? <Loading /> : groups.map(g => (
              <div key={g.key || 'none'} className="flex flex-col gap-1">
                <div className="text-[11px] uppercase tracking-wide text-grayscale-1100 px-1">{g.label}</div>
                {g.items.map(t => {
                  const live = (t.used_by?.triggers || []).filter(x => x.enabled && x.uses !== false).length;
                  const trig = (t.used_by?.triggers || []).length;
                  return (
                    <button key={t.key} type="button" onClick={() => open(t.key)}
                      className={`text-left rounded-lg border px-3 py-2 flex flex-col gap-0.5 ${
                        sel === t.key && !seed ? 'border-primary-500 bg-primary-50' : 'border-grayscale-300 bg-white hover:bg-grayscale-100'}`}>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-medium text-grayscale-1300 break-words">{t.name}</span>
                        <Badge tone="grey">{FORMAT_LABEL[t.format]}</Badge>
                        {t.archived && <Badge tone="red">archived</Badge>}
                        {live > 0 && <Badge tone="amber">live</Badge>}
                      </div>
                      <div className="text-[11px] text-grayscale-1100 truncate">{t.subject}</div>
                      <div className="text-[11px] text-grayscale-1100">
                        v{t.version}
                        {trig ? ` · ${trig} trigger${trig === 1 ? '' : 's'}` : ''}
                        {t.used_by?.campaigns ? ` · ${t.used_by.campaigns} campaign${t.used_by.campaigns === 1 ? '' : 's'}` : ''}
                        {t.origin === 'memac' ? ' · made in MEMAC' : t.origin === 'campaigns' ? ' · made in Campaigns' : ''}
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
            {meta && !list.length && !seed && (
              <div className="text-xs text-grayscale-1100 py-3 text-center">
                No saved templates yet. Press “+ New template”, or “Start from…” to copy a built-in e-mail or a
                past campaign.
              </div>
            )}
            {meta && list.length > 0 && !shown.length && (
              <div className="text-xs text-grayscale-1100 py-3 text-center">No saved template matches.</div>
            )}
          </div>
        </div>

        <div className="min-w-0">
          {sel || seed ? (
            <LibraryTemplateEditor templateKey={sel} meta={meta} origin={origin} seed={sel ? null : seed}
              onSaved={saved} onChanged={() => q.reload()} onDirty={d => { dirty.current = d; }} compact={compact} />
          ) : (
            <div className="text-sm text-grayscale-1100 py-6 text-center">
              {meta ? 'Pick a template, or make a new one.' : ''}
            </div>
          )}
        </div>
      </div>

      {picker && (
        <TemplatePicker onClose={() => setPicker(false)} onPick={src => fromPick(src)}
          title="Start a new template from…" />
      )}
    </Section>
  );
};

export default TemplateLibrary;
